import { createRootRoute, createRoute, createRouter, Outlet, type RouteComponent } from "@tanstack/react-router";
import { useAuth } from "react-oidc-context";
import { AppShell } from "./components/AppShell";
import { HomePage } from "./pages/Home";
import { HospitalsPage } from "./pages/Hospitals";
import { UsersPage } from "./pages/Users";
import { ProfilePage } from "./pages/Profile";
import { ChecklistFormPage } from "./pages/ChecklistForm";
import { ChecklistsOverviewPage } from "./pages/ChecklistsOverview";
import { ChecklistDetailPage } from "./pages/ChecklistDetail";
import { BlankChecklistPage } from "./pages/BlankChecklist";
import { BagChecklistFormPage } from "./pages/BagChecklistForm";
import { BagChecklistsPage } from "./pages/BagChecklists";
import { BagChecklistDetailPage } from "./pages/BagChecklistDetail";
import { UnitsPage } from "./pages/Units";
import { UnitLoginPage } from "./pages/UnitLogin";
import { UnitCodesPage } from "./pages/UnitCodes";
import { AmbulancesPage } from "./pages/Ambulances";
import { PcrReportsPage } from "./pages/PcrReports";
import { PcrReportDetailPage } from "./pages/PcrReportDetail";
import { PcrClosedPage } from "./pages/PcrClosed";
import { ApiClientsPage } from "./pages/ApiClients";
import { WebhooksPage } from "./pages/Webhooks";
import { AuditLogPage } from "./pages/AuditLog";
import { DevelopersPage } from "./pages/Developers";
import { OutboxBanner } from "./components/OutboxBanner";
import { useRealtime } from "./realtime";

/** Pages with their own header (the field form) still require sign-in. */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  if (auth.isLoading) return <p className="p-8 text-slate-500">Checking your session…</p>;
  if (!auth.isAuthenticated) {
    void auth.signinRedirect({ state: { returnTo: window.location.pathname } });
    return null;
  }
  return <>{children}</>;
}

/** Every page: live updates over the WebSocket + the offline/outbox banner. */
function RootLayout() {
  useRealtime();
  return (
    <>
      <OutboxBanner />
      <Outlet />
    </>
  );
}
const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: HomePage });

function Callback() {
  const auth = useAuth();
  if (auth.error) return <p className="p-8 text-red-700">Sign-in error: {auth.error.message}</p>;
  return <p className="p-8 text-slate-500">Completing sign in…</p>;
}
const callbackRoute = createRoute({ getParentRoute: () => rootRoute, path: "/auth/callback", component: Callback });

// Signed-in area (header + side nav). Paths match the legacy app so bookmarks keep working.
const appRoute = createRoute({ getParentRoute: () => rootRoute, id: "app", component: AppShell });
// Generic keeps the literal path type so <Link to="..."> stays type-checked.
function page<P extends string>(path: P, component: RouteComponent) {
  return createRoute({ getParentRoute: () => appRoute, path, component });
}

const appChildren = [
  page("/Checklists-Overview", ChecklistsOverviewPage),
  page("/Checklists-Overview/$id", ChecklistDetailPage),
  page("/bag-checklists", BagChecklistsPage),
  page("/bag-checklists/$id", BagChecklistDetailPage),
  page("/units", UnitsPage),
  page("/pcr-reports", PcrReportsPage),
  page("/pcr-reports/$id", PcrReportDetailPage),
  page("/hospitals", HospitalsPage),
  page("/ambulances", AmbulancesPage),
  page("/unit-codes", UnitCodesPage),
  page("/users", UsersPage),
  page("/api-clients", ApiClientsPage),
  page("/webhooks", WebhooksPage),
  page("/audit-log", AuditLogPage),
  page("/developers", DevelopersPage),
  page("/profile", ProfilePage),
];

const checklistRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/checklist",
  component: () => (
    <RequireAuth>
      <ChecklistFormPage />
    </RequireAuth>
  ),
});
const blankChecklistRoute = createRoute({ getParentRoute: () => rootRoute, path: "/blank-checklist", component: BlankChecklistPage });
const bagChecklistRoute = createRoute({ getParentRoute: () => rootRoute, path: "/bag-checklist", component: () => (<RequireAuth><BagChecklistFormPage /></RequireAuth>) });
// PCR is closed: /pcr shows the notice; /blank-pcr redirects there (old bookmarks).
const pcrClosedRoute = createRoute({ getParentRoute: () => rootRoute, path: "/pcr", component: PcrClosedPage });
const blankPcrRoute = createRoute({ getParentRoute: () => rootRoute, path: "/blank-pcr", component: PcrClosedPage });
const unitLoginRoute = createRoute({ getParentRoute: () => rootRoute, path: "/unit-login", component: () => (<RequireAuth><UnitLoginPage /></RequireAuth>) });

const routeTree = rootRoute.addChildren([indexRoute, callbackRoute, checklistRoute, blankChecklistRoute, bagChecklistRoute, unitLoginRoute, pcrClosedRoute, blankPcrRoute, appRoute.addChildren(appChildren)]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
